import { withFormProps } from '@kizi/comparator-components';
import { useField, usePage, useWizardService } from '@kizi/comparator-core';
import { Div, Label } from '@kizi/core-components';
import React, { useEffect, useMemo } from 'react';
import { CostInfo, Option, transformCost } from '../../utils/extraOptions';
import { SummaryCostInfo } from '../wrappers/ProductSummaryWrapper';
import { Select } from './Select';

const DivWithFormProps = withFormProps(Div);

interface ExtraOptionsSelectProps {
  _label: string;
  _options: Option[];
  name: string;
}

export const ExtraOptionsSelect: React.FC<ExtraOptionsSelectProps> = ({
  _label,
  _options,
  name,
  ...props
}) => {
  const field = useField(name);
  const wizard = useWizardService();
  const { state, setState } = usePage();

  const setProductSummaryOption = (value: string | undefined) => {
    if (!state || !setState || !value) return;

    const cost = _options.find((option) => option.value === value)
      ?.cost as CostInfo;

    const costType = _options.find(
      (option) => option?.cost?.monthly?.items?.length > 0,
    )
      ? 'monthly'
      : 'onceOff';

    const data = (state[costType] || []) as SummaryCostInfo[];
    const index = data.findIndex(
      (extraOption) => extraOption.fieldName === name,
    );

    if (index > -1 || !value) {
      if (cost) {
        data[index] = {
          fieldName: name,
          name: cost.name,
          rates: cost[costType].items,
        };
      } else {
        data.splice(index, 1);
      }
    } else {
      data.push({
        fieldName: name,
        name: cost?.name,
        rates: cost[costType].items,
      });
    }

    setState({ ...state, [costType]: data });
  };

  const transformedOptions = useMemo(
    () =>
      _options.map(({ cost, label, value }) => {
        const priceToAdd = cost ? `voor ${transformCost(cost)}` : '';
        const title = `${label} ${priceToAdd}`;

        return { label: title, value };
      }),
    [_options],
  );

  useEffect(() => {
    return () => {
      if (!wizard?.getSnapshot()?.event?.type?.includes('done')) {
        field?.set({ value: undefined });
        setProductSummaryOption(undefined);
      }
    };
  }, []);

  return (
    <DivWithFormProps
      _errorProps={{ variant: 'error' }}
      _successProps={{ variant: 'success' }}
      {...props}
      _containerProps={{ pb: 0 }}
      display="flex"
      flexDirection="column"
    >
      <Label>{_label}</Label>
      <Select
        value={field?.value}
        options={transformedOptions}
        onChange={(e) => {
          field?.set({ value: e.target.value });
          setProductSummaryOption(e.target.value);
        }}
      />
    </DivWithFormProps>
  );
};
